import React, { useState, useEffect } from 'react';
import { useTypedTranslation } from '@app/hooks';
import { PasslySelect, PasslyTextField } from '@app/components';
import { IStringFilter, StringFilterTypes } from '@app/models';
import { createStringFilters as filters } from '../../methods';

interface IProps {
    checkValidation: Function;
    patchData: IData;
}

interface IAttribute {
    filter: IStringFilter | null;
    value: string;
}

interface IData {
    keyFilter: {
        value: StringFilterTypes
    };
    valueFilter: {
        value: StringFilterTypes
    };
    key?: string;
    value?: string;
}

enum AttributeType {
    name = 'name',
    value = 'value'
}

export const AttributeSelector = (props: IProps) => {
    const { translation } = useTypedTranslation();
    const { checkValidation, patchData } = props;

    const [name, setName] = useState<IAttribute>({ filter: null, value: '' });
    const [value, setValue] = useState<IAttribute>({ filter: null, value: '' });

    const handleFilterChange = (type: AttributeType, event: React.ChangeEvent<{ value: unknown }>) => {
        const value = event.target.value as string;
        changeStateFilter(type, value);
    };

    const handleInputChange = (type: AttributeType, event: React.ChangeEvent<{ value: unknown }>) => {
        const value = event.target.value as string;
        changeStateValue(type, value);
    };

    const changeStateFilter = (type: AttributeType, value: string | undefined): void => {
        const selectedFilter = filters.find((filter: IStringFilter) => filter.value === value);
        if (selectedFilter !== null && selectedFilter !== undefined) {
            switch (type) {
                case AttributeType.name:
                    setName(prevState => ({ ...prevState, filter: selectedFilter }));
                    break;

                case AttributeType.value:
                    setValue(prevState => ({ ...prevState, filter: selectedFilter }));
                    break;

                default:
                    break;
            }
        }
    };

    const changeStateValue = (type: AttributeType, value: string | undefined): void => {
        switch (type) {
            case AttributeType.name:
                setName(prevState => ({ ...prevState, value: value ?? '' }));
                break;

            case AttributeType.value:
                setValue(prevState => ({ ...prevState, value: value ?? '' }));
                break;

            default:
                break;
        }
    };

    useEffect(() => {
        changeStateFilter(AttributeType.name, patchData?.keyFilter?.value);
        changeStateValue(AttributeType.name, patchData?.key);
        changeStateFilter(AttributeType.value, patchData?.valueFilter?.value);
        changeStateValue(AttributeType.value, patchData?.value);
    }, []);

    useEffect(() => {
        if (name.filter !== null && value.filter !== null) {
            const data: IData = {
                keyFilter: {
                    value: name.filter.value
                },
                valueFilter: {
                    value: value.filter.value
                }
            };
            if (name.filter.requiresValue) data.key = name.value;
            if (value.filter.requiresValue) data.value = value.value;
            checkValidation(true, data);
        } else {
            checkValidation(false, null);
        }
    }, [name, value]);

    return (
        <div className='attributeSelector'>
            <div className='attributeSelector__row'>
                <PasslySelect
                    id='attributeSelectKey'
                    label={'AUTH_POLICY_ATTR_KEY_FILTER_LABEL'}
                    value={name.filter !== null ? name.filter.value : ''}
                    onChange={(event: any) => handleFilterChange(AttributeType.name, event)}
                    options={filters}
                />
                {name.filter !== null && name.filter.requiresValue &&
                    <PasslyTextField
                        id='attributeKey'
                        placeholder={translation('EXAMPLE_DEPT')}
                        value={name.value}
                        onChange={(event: any) => handleInputChange(AttributeType.name, event)}
                    />
                }
            </div>
            <div className='attributeSelector__row'>
                <PasslySelect
                    id='attributeSelectValue'
                    label={'AUTH_POLICY_ATTR_VALUE_FILTER_LABEL'}
                    value={value.filter !== null ? value.filter.value : ''}
                    onChange={(event: any) => handleFilterChange(AttributeType.value, event)}
                    options={filters}
                />
                {value.filter !== null && value.filter.requiresValue &&
                    <PasslyTextField
                        id='attributeValue'
                        placeholder={translation('EXAMPLE_SALES')}
                        value={value.value}
                        onChange={(event: any) => handleInputChange(AttributeType.value, event)}
                    />
                }
            </div>
        </div>
    );
};
